<template>
  <div :class="theme_.container">
    <div
      v-for="(bar, index) in bars"
      :key="index"
      class="flex w-full flex-col items-start gap-1"
    >
      <div
        v-if="bar.label"
        class="flex w-full items-center justify-between"
        :class="theme_.label"
        @click="click(index)"
        v-html="bar.label"
      />
      <div
        class="flex w-full flex-nowrap gap-[2px] overflow-hidden"
        :class="[...theme_.background, ...(bar.theme.background || [])]"
        @click="click(index)"
      >
        <div
          v-for="(progress, j) in bar.progresses"
          :key="j"
          v-tooltip="
            showTooltips &&
            (progress.tooltip || progress.width) && {
              content: progress.tooltip || progress.width,
              placement: 'bottom',
            }
          "
          class="h-full"
          :class="[
            ...theme_.progress,
            ...(bar.theme.progress || progress.theme || []),
          ]"
          :style="{ width: progress.width }"
        />
      </div>
      <div
        v-if="bar.legend"
        class="flex w-full items-center justify-start gap-3 md:gap-4"
        :class="theme_.legend"
        @click="click(index)"
        v-html="bar.legend"
      />
    </div>
  </div>
</template>
<script>
const DEFAULT_THEME = {
  container: ['flex', 'flex-col', 'gap-3'],
  background: ['h-4', 'bg-gray-200', 'rounded-sm'],
  label: ['text-xs', 'leading-4', 'font-medium'],
  progress: ['bg-blue-600', 'hover:bg-blue-700', 'rounded-sm'],
}

/**
 * # Componente que gera um gráfico de barras.
 *
 * ## As props theme e decimalPlaces:
 *
 * 1. A prop theme é um objeto onde cada chave é um array de classes css. As chaves válidas são: container, background, label, progress.
 *  Default:
 *```
 *  {
 *    container: ['flex', 'flex-col', 'gap-3'],
 *    background: ['h-4', 'bg-gray-200', 'rounded-sm'],
 *    label: ['text-xs', 'leading-4', 'font-medium'],
 *    progress: ['bg-blue-600', 'hover:bg-blue-700', 'rounded-sm']
 *  }
 *```
 *
 * 2. A prop decimalPlaces determina o número de casas decimais que serão consideradas nas porcentagens que são exibidas nos gráficos. O padrão é 2.
 *
 * --------------------------------------------------------------
 *
 * ## A prop data:
 *
 * 1. O gráfico de barras pode ter 1 a n barras. O que vai determinar isso é a quantidade de objetos que forem passados no array da prop `data`.
 *
 * 2. Cada objeto deve seguir o seguinte formato:
 *
 *```
 * {
 *   theme: Object (optional -  chaves válidas: background, progress),
 *   label: String || function (bar, progresses): string {},
 *   x: Number || Array<Object>, (required)
 *   y: Number (optional*),
 * }
 *```
 *
 * 3. O atributo label pode ser uma simples string, ou uma função que deve retornar uma string. Também é aceito Html.
 *
 * 4. O atributo y é um número que representa o todo. Quando y não for passado (undefined, null, 0, false),
 * vamos considerar que o x é a porcentagem já calculada.
 *
 * 5. a) O atributo x representa uma parte do todo.
 *    Ex: Se o total é 50, e você consumiu 25, x será 25 e y será 50.
 *        O componente calculará a porcentagem automaticamente para fazer crescer a barra de progresso.
 *    b) Quando y não é passado, como informado em 4, x é um número que representa a porcentagem do todo que foi alcançada.
 *    Ex: Se a porcentagem for 45%, o valor de x será 45 e não haverá valor de y válido.
 *
 * 6. a) Caso você tenha diferentes progressos na mesma barra, você deverá passar um array de objetos para o atributo X.
 * Isso é útil, por exemplo, para quando você tem vendas mensais, de diferentes vendedores, e quer exibir o total de vendas de cada mês e
 * destacar a porcentagem de cada vendedor, em cada mês (Ex: https://exceleasy.com.br/wp-content/uploads/2020/03/Gr%C3%A1fico-de-colunas-empilhadas.png).
 * Para tal, você vai passar um Array em x, da seguinte forma:
 *
 *```
 * [
 *   {
 *     value: Number (required),
 *     theme: Array<css classes> (optional)
 *   }
 * ]
 *```
 *
 *    b) O atributo value obedece as regras definidas em 5.
 *
 *    c) O atributo theme é um array de classes css. Através dele, você consegue colorir cada progresso, por exemplo.
 */
export default {
  name: 'AppBarChart',
  props: {
    theme: {
      type: Object,
      default: () => DEFAULT_THEME,
    },
    data: {
      type: [Array, Object],
      default: () => [],
    },
    decimalPlaces: {
      type: Number,
      default: 2,
    },
    showTooltips: {
      type: Boolean,
      default: true,
    },
    smallestBarSize: {
      type: Number,
      default: 0.5,
    },
  },
  emits: ['click'],
  computed: {
    theme_() {
      return {
        ...DEFAULT_THEME,
        ...this.theme,
      }
    },
    bars() {
      if (Array.isArray(this.data)) {
        return this.data.map((bar) => this.normalizeBar(bar))
      }
      return [this.normalizeBar(this.data)]
    },
  },
  methods: {
    click(index) {
      this.$emit('click', this.data[index])
    },
    normalizeBar(bar) {
      const normalize_ = ({ value, theme, tooltip }) => {
        const percentage = bar.y ? (value * 100) / (bar.y || 1) : value
        const width = `${
          percentage < this.smallestBarSize && percentage > 0
            ? this.smallestBarSize
            : percentage.toFixed(this.decimalPlaces)
        }%`
        return {
          tooltip,
          percentage,
          width,
          theme,
        }
      }

      const progresses = Array.isArray(bar.x)
        ? bar.x.map(normalize_)
        : [normalize_({ ...bar, value: bar.x })]

      const label =
        typeof bar.label === 'function'
          ? bar.label({ ...bar, progresses })
          : bar.label

      const legend =
        typeof bar.legend === 'function'
          ? bar.legend({ ...bar, progresses })
          : bar.legend

      return {
        label,
        legend,
        progresses,
        theme: bar.theme || {},
      }
    },
  },
}
</script>
